import type { LocationQuery } from 'vue-router';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import type { Props as TagListInterface } from '@hypercodestudio/basler-components/dist/components/elements/taglist/TagList.vue';
import type { MediaItemInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/Media.vue';
import type { Props as TeaserCardProps } from '@hypercodestudio/basler-components/dist/components/modules/TeaserCard.vue';
import type { AlgoliaContentfulPage } from '~/lib/AlgoliaService/models/AlgoliaContentfulPage';
import type { AlgoliaProduct } from '~/lib/AlgoliaService/models/AlgoliaProduct';
import type { SearchTagModel } from '~/utils/createDictKeysForSearchTags';
import { isAlgoliaContentfulPage } from '~/utils/guards/isAlgoliaContentfulPage';
import { isAlgoliaProduct } from '~/utils/guards/isAlgoliaProduct';
import { createAlgoliaProductImage } from '~/utils/createAlgoliaProductImage';
import { buildLinkInterface } from '~/utils/buildLinkInterface';
import { buildPdpUrl } from '~/utils/buildPdpUrl';
import { transformAlgoliaContentfulPageToTeaserCard } from '~/utils/transformAlgoliaContentfulPageToTeaserCard';
import { DOCUMENTS_TAG, PRODUCTS_TAG } from '~/lib/AlgoliaService/constants';
import { dotString } from '~/utils/dotString';

import {
  type DownloadEntryModel,
  isDownloadEntryModel
} from '~/lib/DownloadService/model';

export function transformSearchResultToTeaserCard(
  result: AlgoliaContentfulPage | AlgoliaProduct | DownloadEntryModel,
  tags: ReadonlyArray<SearchTagModel>,
  locale: string,
  query?: LocationQuery
): TeaserCardProps | null {
  if (isAlgoliaContentfulPage(result)) {
    const transformed = transformAlgoliaContentfulPageToTeaserCard(
      result,
      locale
    );
    // XXX: the teaser card is also used in the tag overview
    //  Might pass an option to the function instead of manipulating the result.
    if (transformed) {
      // Get search category tags (formatUseCase, industryApplications, etc.)
      const searchCategoryTags = tags
        .filter((tag) => result.tags.includes(tag.value))
        .map((tag) => ({
          text: tag.label ?? tag.value
        }));

      // Get content-specific tags from detailed_topics (already processed in transformAlgoliaContentfulPageToTeaserCard)
      const contentTags = transformed.tagList?.tags || [];

      // Combine both types of tags, with content tags first
      transformed.tagList = {
        tags: [...contentTags, ...searchCategoryTags],
        isSmall: true,
        isNotWrapped: false
      };
    }
    transformed.teaserCardDataAttributes = {
      'insights-object-id': result.objectID,
      'insights-position': result.position
    };
    return transformed;
  }
  if (isAlgoliaProduct(result)) {
    return transformAlgoliaProductToTeaserCard(result, tags, locale);
  }
  if (isDownloadEntryModel(result)) {
    return transformDocumentToTeaserCard(result, tags, query);
  }

  return null;
}

export function transformAlgoliaProductToTeaserCard(
  product: AlgoliaProduct,
  tags: ReadonlyArray<SearchTagModel>,
  locale: string
): TeaserCardProps {
  const media: MediaItemInterface = createAlgoliaProductImage(product, {
    keepAspectRatio: false,
    widths: [415, 830]
  }) ?? {
    type: 'image',
    content: {
      src: product.thumbnail_url ?? ''
    }
  };
  const tagList: TagListInterface = {
    tags: tags
      .filter((tag) => tag.value === PRODUCTS_TAG)
      .map((tag) => ({
        text: tag.label ?? tag.value
      })),
    isSmall: true,
    size: 'small'
  };

  return {
    content: product.seo_headline ?? '',
    header: media,
    tagList,
    headerGutter: false,
    headline: product.name ?? '',
    linkIconPosition: 'content',
    link: buildLinkInterface(buildPdpUrl(locale, product.url_key))
  };
}

export function transformDocumentToTeaserCard(
  download: DownloadEntryModel,
  tags: ReadonlyArray<SearchTagModel>,
  query?: LocationQuery
): TeaserCardProps {
  const link: HyperlinkInterface = {
    uri: {
      hash: `#download=${download.id}`,
      query
    }
  };

  const tagList: TagListInterface = {
    tags: tags
      .filter((tag) => tag.value === DOCUMENTS_TAG)
      .map((tag) => ({
        text: tag.label ?? tag.value
      })),
    isSmall: true,
    isNotWrapped: false,
    size: 'small'
  };

  return {
    content: dotString(download.metaDescription ?? '', 350),
    header: null,
    badge: download.downloadCategory,
    tagList,
    headerGutter: false,
    headline: download.name,
    linkIconPosition: 'content',
    link
  };
}
